import { useTranslation } from "@shared/utils/internationalization";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import Flex from "@ui-kit/layout/Flex/Flex.web";
import Button from "@ui-kit/form/Button/Button.web";
import Input, { InputGroup } from "@ui-kit/form/Input/Input.web";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useItemPasswordEntry } from "./useItemPasswordEntry";
import { ItemPasswordEntryProps } from "./ItemPasswordEntry.props";

type FormValues = {
  password: string;
};

const ItemPasswordEntry = ({
  item,
  slugToCheck,
  authArray,
  updateAuthArray,
  buttonVariant = "solid",
  buttonVerbiage = undefined,
  buttonMaxWidth = undefined,
  formLabel = undefined,
}: ItemPasswordEntryProps) => {
  const { t } = useTranslation("common");
  const { isPasswordProtected, isUnauthorized, passwordSchema, onSubmit } =
    useItemPasswordEntry(item, authArray, updateAuthArray, slugToCheck);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, errors, touchedFields },
  } = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - types are correct for yupResolver
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      password: "",
    },
  });

  const handleFormSubmit = async (data: FormValues) => {
    // Adapt the onSubmit from the hook to work with React Hook Form
    return new Promise<void>((resolve) => {
      onSubmit(data, {
        setSubmitting: () => {
          resolve();
        },
      });
    });
  };

  return isPasswordProtected && isUnauthorized ? (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <FormLabel pt="lg" htmlFor="password" fontSize="sm">
        {formLabel ?? t("Enter password to select tickets")}
      </FormLabel>
      <Flex direction="row" width="100%">
        <Flex direction="column" justifyContent="flex-end" mr="lg" width="100%">
          <FormControl
            isInvalid={Boolean(errors.password && touchedFields.password)}
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <InputGroup size="md">
                  <Input
                    {...field}
                    maxHeight="element.3xl"
                    id="password"
                    placeholder={t("Enter password")}
                    type="password"
                    autoComplete="off"
                    width="100%"
                    backgroundColor="bg"
                  />
                </InputGroup>
              )}
            />
          </FormControl>
        </Flex>
        <Flex direction="column" justifyContent="flex-end">
          <Button
            data-testid="item-password-entry-submit"
            variant={buttonVariant}
            colorPalette="primary"
            minWidth={buttonMaxWidth ?? { base: "120px", md: "140px" }}
            isLoading={isSubmitting}
            disabled={isSubmitting || !isValid}
            type="submit"
          >
            {buttonVerbiage ?? t("Submit", { ns: "common" })}
          </Button>
        </Flex>
      </Flex>
    </form>
  ) : (
    <></>
  );
};

export default ItemPasswordEntry;
